import { useState, useEffect } from 'react';
import '@assets/css/common/style_main.css';
import '@assets/css/common/style_table.css';
import '@assets/css/common/style_modal.css';
import '@assets/css/common/style_calendar.css'; // 계정권한 그룹 관리와 동일한 CSS 사용
import branchApi from '@services/branchApi';

const BranchManagement = () => {
  const [branches, setBranches] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filterType, setFilterType] = useState('latest');
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [pageSize] = useState(10);

  // 모달 상태
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');

  // 선택된 지점 정보
  const [selectedBranch, setSelectedBranch] = useState(null);

  // 폼 입력값
  const [formData, setFormData] = useState({
    name: '',
    country: 'KR',
    city: '',
    district: '',
    address: '',
    postalCode: '',
    latitude: '',
    longitude: '',
    managerName: '',
    managerPhone: '',
    managerEmail: '',
    businessHours: '',
    timeZone: 'Asia/Seoul',
    openDate: '',
    closedDate: '',
    status: 'ACTIVE'
  });

  // 날짜 포맷팅 함수
  const formatDate = (dateString) => {
    if (!dateString) return '';
    return dateString.split('T')[0];
  };

  // 더미 데이터 (API 실패 시 폴백용)
  const dummyData = [
    {
      id: 1,
      branchCode: 'KR000001',
      name: '강남지점',
      country: 'KR',
      city: '서울',
      district: '강남구',
      address: '123 강남대로',
      postalCode: '06236',
      latitude: '37.4979',
      longitude: '127.0276',
      managerName: '홍길동',
      managerPhone: '010-1234-5678',
      managerEmail: 'manager1@example.com',
      businessHours: '월-금 09:00~18:00',
      timeZone: 'Asia/Seoul',
      openDate: '2023-01-02',
      closedDate: null,
      status: 'ACTIVE',
      createdAt: '2025-03-25 23:56:26',
      createdBy: 'admin',
      updatedAt: '2025-03-25 23:56:26',
      updatedBy: 'admin'
    },
    {
      id: 2,
      branchCode: 'KR000002',
      name: '해운대지점',
      country: 'KR',
      city: '부산',
      district: '해운대구',
      address: '456 해운대로',
      postalCode: '48095',
      latitude: '35.1631',
      longitude: '129.1635',
      managerName: '김철수',
      managerPhone: '010-8765-4321',
      managerEmail: 'manager2@example.com',
      businessHours: '월-금 10:00~17:00',
      timeZone: 'Asia/Seoul',
      openDate: '2023-03-15',
      closedDate: null,
      status: 'ACTIVE',
      createdAt: '2025-03-25 23:56:26',
      createdBy: 'admin',
      updatedAt: '2025-03-25 23:56:26',
      updatedBy: 'admin'
    }
  ];

  // 컴포넌트 마운트 시 데이터 로드
  useEffect(() => {
    loadBranches();
  }, [currentPage, filterType]);

  const loadBranches = async () => {
    setLoading(true);
    setError(null);

    try {
      // 정렬 매개변수 설정
      let sortParam = 'createdAt,desc'; // 기본값: 최신순
      if (filterType === 'name') {
        sortParam = 'name,asc';
      } else if (filterType === 'city') {
        sortParam = 'city,asc';
      }

      // API 호출
      const response = await branchApi.getBranches({
        page: currentPage,
        size: pageSize,
        sort: sortParam
      });

      if (response?.success && response?.data) {
        const { content, totalPages: pages, totalElements: elements } = response.data;
        setBranches(content || []);
        setTotalPages(pages || 0);
        setTotalElements(elements || 0);
      } else {
        // API 응답이 예상과 다른 경우 더미 데이터 사용
        console.warn('Unexpected API response, using dummy data');
        setBranches(dummyData);
        setTotalPages(1);
        setTotalElements(dummyData.length);
      }
    } catch (error) {
      console.error('Failed to load branches:', error);
      setError('지점 목록을 불러오는데 실패했습니다.');
      // 에러 시 더미 데이터 표시
      setBranches(dummyData);
      setTotalPages(1);
      setTotalElements(dummyData.length);
    } finally {
      setLoading(false);
    }
  };

  // 전체 선택/해제
  const handleSelectAll = (e) => {
    const checked = e.target.checked;
    setSelectAll(checked);
    if (checked) {
      setSelectedItems(branches.map(branch => branch.id));
    } else {
      setSelectedItems([]);
    }
  };

  // 개별 선택
  const handleItemSelect = (id, e) => {
    e.stopPropagation();
    if (selectedItems.includes(id)) {
      setSelectedItems(selectedItems.filter(item => item !== id));
      setSelectAll(false);
    } else {
      const newSelected = [...selectedItems, id];
      setSelectedItems(newSelected);
      setSelectAll(newSelected.length === branches.length);
    }
  };

  // 필터 변경
  const handleFilterChange = (e) => {
    setFilterType(e.target.value);
    setCurrentPage(0);
  };

  // 추가 모달 열기
  const handleAddClick = (e) => {
    e.preventDefault();
    setFormData({
      name: '',
      country: 'KR',
      city: '',
      district: '',
      address: '',
      postalCode: '',
      latitude: '',
      longitude: '',
      managerName: '',
      managerPhone: '',
      managerEmail: '',
      businessHours: '',
      timeZone: 'Asia/Seoul',
      openDate: '',
      closedDate: '',
      status: 'ACTIVE'
    });
    setIsEdit(false);
    setShowAddModal(true);
  };

  // 수정 모달 열기
  const handleEditClick = (branch) => {
    setSelectedBranch(branch);
    setFormData({
      name: branch.name,
      country: branch.country,
      city: branch.city,
      district: branch.district,
      address: branch.address,
      postalCode: branch.postalCode,
      latitude: branch.latitude,
      longitude: branch.longitude,
      managerName: branch.managerName,
      managerPhone: branch.managerPhone,
      managerEmail: branch.managerEmail,
      businessHours: branch.businessHours,
      timeZone: branch.timeZone,
      openDate: formatDate(branch.openDate) || '',
      closedDate: formatDate(branch.closedDate) || '',
      status: branch.status
    });
    setIsEdit(true);
    setShowEditModal(true);
  };

  // 상세정보 모달 열기
  const handleInfoClick = (branch) => {
    setSelectedBranch(branch);
    setShowInfoModal(true);
  };

  // 삭제 확인 모달 열기
  const handleDeleteClick = (branch) => {
    setSelectedBranch(branch);
    setShowDeleteModal(true);
  };

  // 삭제 실행
  const handleDeleteConfirm = async () => {
    try {
      const response = await branchApi.deleteBranch(selectedBranch.id);

      if (response?.success) {
        // 목록 새로고침
        await loadBranches();
        setShowDeleteModal(false);
        setAlertMessage('지점이 삭제되었습니다.');
      } else {
        setAlertMessage(response?.message || '지점 삭제에 실패했습니다.');
      }
    } catch (error) {
      console.error('Failed to delete branch:', error);
      setAlertMessage('지점 삭제 중 오류가 발생했습니다.');
    }
    setShowAlertModal(true);
    setShowDeleteModal(false);
  };

  // 일괄 삭제
  const handleBulkDelete = async () => {
    if (selectedItems.length === 0) {
      setAlertMessage('삭제할 항목을 선택해주세요.');
      setShowAlertModal(true);
      return;
    }

    try {
      const result = await branchApi.deleteBranches(selectedItems);

      if (result.success) {
        setAlertMessage(`${result.succeeded}개의 지점이 삭제되었습니다.`);
      } else {
        setAlertMessage(`${result.succeeded}개 삭제 성공, ${result.failed}개 삭제 실패`);
      }

      // 목록 새로고침
      await loadBranches();
      setSelectedItems([]);
      setSelectAll(false);
    } catch (error) {
      console.error('Failed to delete branches:', error);
      setAlertMessage('지점 삭제 중 오류가 발생했습니다.');
    }
    setShowAlertModal(true);
  };

  // 폼 입력값 변경
  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // 추가/수정 확인
  const handleSaveConfirm = async () => {
    // 필수값 검증
    if (!formData.name) {
      setAlertMessage('지점명은 필수입니다.');
      setShowAlertModal(true);
      return;
    }

    if (!formData.status) {
      setAlertMessage('상태는 필수입니다.');
      setShowAlertModal(true);
      return;
    }

    try {
      let response;

      if (isEdit) {
        // 수정
        response = await branchApi.updateBranch(selectedBranch.id, formData);
      } else {
        // 추가
        response = await branchApi.createBranch(formData);
      }

      if (response?.success) {
        // 목록 새로고침
        await loadBranches();
        setShowEditModal(false);
        setShowAddModal(false);
        setAlertMessage(isEdit ? '지점 정보가 수정되었습니다.' : '새 지점이 추가되었습니다.');
      } else {
        setAlertMessage(response?.message || (isEdit ? '지점 수정에 실패했습니다.' : '지점 추가에 실패했습니다.'));
      }
    } catch (error) {
      console.error('Failed to save branch:', error);
      setAlertMessage(isEdit ? '지점 수정 중 오류가 발생했습니다.' : '지점 추가 중 오류가 발생했습니다.');
    }
    setShowAlertModal(true);
  };

  // 모든 모달 닫기
  const handleCloseAllModals = () => {
    setShowAddModal(false);
    setShowEditModal(false);
    setShowInfoModal(false);
    setShowDeleteModal(false);
    setShowAlertModal(false);
    setSelectedBranch(null);
  };

  // 페이지네이션
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  return (
    <>
      <div className="page-title-container">
        <h1 className="page-title">지점 관리</h1>
        <div className="page-title-icon">
          <a href="#" id="add-theme-btn" title="지점 추가" onClick={handleAddClick}>
            <i className="ri-add-circle-line"></i>
          </a>
        </div>
      </div>

      <div className="page-container">
        {/* 필터 드롭다운 */}
        <div className="filter-dropdown-container-row">
          {/* 일괄 삭제 버튼 */}
          <div className={`bulk-delete-container ${selectedItems.length >= 2 ? 'active' : ''}`}>
            <button className="bulk-delete-btn" onClick={handleBulkDelete}>
              일괄 삭제 ({selectedItems.length})
            </button>
          </div>

          <div className="filter-dropdown">
            <select
              id="branch-filter"
              value={filterType}
              onChange={handleFilterChange}
            >
              <option value="latest">최신순</option>
              <option value="name">지점명순</option>
              <option value="city">도시순</option>
            </select>
          </div>
        </div>

        {/* 지점 테이블 */}
        <div className="common-table-container">
          {loading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>데이터를 불러오는 중입니다...</p>
            </div>
          ) : error ? (
            <div className="error-message">
              <p>{error}</p>
              <button className="retry-button" onClick={loadBranches}>
                다시 시도
              </button>
            </div>
          ) : (
            <table className="common-table">
              <thead>
                <tr>
                  <th>
                    <div className="checkbox-container">
                      <input
                        type="checkbox"
                        id="select-all"
                        checked={selectAll}
                        onChange={handleSelectAll}
                      />
                      <label htmlFor="select-all" className="custom-checkbox"></label>
                    </div>
                  </th>
                  <th>NO</th>
                  <th>지점 코드</th>
                  <th>지점명</th>
                  <th>국가</th>
                  <th>도시</th>
                  <th>상태</th>
                  <th>상세정보</th>
                  <th>수정</th>
                  <th>삭제</th>
                </tr>
              </thead>
              <tbody>
                {branches.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="no-data-message">
                      등록된 지점이 없습니다.
                    </td>
                  </tr>
                ) : (
                  branches.map((branch, index) => (
                    <tr key={branch.id}>
                      <td>
                        <div className="checkbox-container">
                          <input
                            type="checkbox"
                            id={`branch-${branch.id}`}
                            checked={selectedItems.includes(branch.id)}
                            onChange={(e) => handleItemSelect(branch.id, e)}
                          />
                          <label htmlFor={`branch-${branch.id}`} className="custom-checkbox"></label>
                        </div>
                      </td>
                      <td>{index + 1}</td>
                      <td>{branch.branchCode}</td>
                      <td>{branch.name}</td>
                      <td>{branch.country}</td>
                      <td>{branch.city}</td>
                      <td>
                        <span className={`status-badge ${branch.status === 'ACTIVE' ? 'active' : 'inactive'}`}>
                          {branch.status}
                        </span>
                      </td>
                      <td>
                        <button
                          className="info-btn"
                          onClick={() => handleInfoClick(branch)}
                        >
                          <i className="ri-information-line"></i>
                        </button>
                      </td>
                      <td>
                        <button
                          className="edit-btn"
                          onClick={() => handleEditClick(branch)}
                        >
                          <i className="ri-pencil-line"></i>
                        </button>
                      </td>
                      <td>
                        <button
                          className="delete-btn"
                          onClick={() => handleDeleteClick(branch)}
                        >
                          <i className="ri-close-line"></i>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}

          {/* 페이지네이션 */}
          {totalPages > 1 && (
            <div className="pagination">
              <button
                className="page-btn"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 0}
              >
                이전
              </button>

              {[...Array(totalPages)].map((_, i) => (
                <button
                  key={i}
                  className={`page-btn ${currentPage === i ? 'active' : ''}`}
                  onClick={() => handlePageChange(i)}
                >
                  {i + 1}
                </button>
              ))}

              <button
                className="page-btn"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages - 1}
              >
                다음
              </button>
            </div>
          )}
        </div>
      </div>

      {/* 추가 모달 */}
      {showAddModal && (
        <div className="modal-overlay active" id="create-branch-modal">
          <div className="modal-container">
            <div className="modal-header">
              <h3 className="modal-title">지점 추가</h3>
              <button className="modal-close" onClick={handleCloseAllModals}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="modal-form-group">
                <label className="modal-label">지점명 *</label>
                <input
                  type="text"
                  className="modal-input"
                  value={formData.name}
                  onChange={(e) => handleFormChange('name', e.target.value)}
                  placeholder="지점명을 입력하세요"
                />
              </div>

              <div className="modal-form-group">
                <label className="modal-label">국가코드 *</label>
                <input
                  type="text"
                  className="modal-input"
                  value={formData.country}
                  onChange={(e) => handleFormChange('country', e.target.value.toUpperCase())}
                  placeholder="예: KR, US, JP"
                  maxLength="2"
                />
              </div>

              <div className="modal-form-group">
                <label className="modal-label">도시</label>
                <input
                  type="text"
                  className="modal-input"
                  value={formData.city}
                  onChange={(e) => handleFormChange('city', e.target.value)}
                  placeholder="예: 서울"
                />
              </div>

              <div className="modal-form-group">
                <label className="modal-label">구/군</label>
                <input
                  type="text"
                  className="modal-input"
                  value={formData.district}
                  onChange={(e) => handleFormChange('district', e.target.value)}
                  placeholder="예: 강남구"
                />
              </div>

              <div className="modal-form-group">
                <label className="modal-label">주소</label>
                <input
                  type="text"
                  className="modal-input"
                  value={formData.address}
                  onChange={(e) => handleFormChange('address', e.target.value)}
                  placeholder="상세 주소를 입력하세요"
                />
              </div>

              <div className="modal-form-group">
                <label className="modal-label">관리자명</label>
                <input
                  type="text"
                  className="modal-input"
                  value={formData.managerName}
                  onChange={(e) => handleFormChange('managerName', e.target.value)}
                  placeholder="관리자 이름"
                />
              </div>

              <div className="modal-form-group">
                <label className="modal-label">연락처</label>
                <input
                  type="text"
                  className="modal-input"
                  value={formData.managerPhone}
                  onChange={(e) => handleFormChange('managerPhone', e.target.value)}
                  placeholder="010-0000-0000"
                />
              </div>

              <div className="modal-form-group">
                <label className="modal-label">이메일</label>
                <input
                  type="email"
                  className="modal-input"
                  value={formData.managerEmail}
                  onChange={(e) => handleFormChange('managerEmail', e.target.value)}
                  placeholder="manager@example.com"
                />
              </div>

              <div className="modal-form-group">
                <label className="modal-label">영업시간</label>
                <input
                  type="text"
                  className="modal-input"
                  value={formData.businessHours}
                  onChange={(e) => handleFormChange('businessHours', e.target.value)}
                  placeholder="예: 월-금 09:00~18:00"
                />
              </div>

              <div className="modal-form-group">
                <label className="modal-label">오픈일</label>
                <input
                  type="date"
                  className="modal-input"
                  value={formData.openDate}
                  onChange={(e) => handleFormChange('openDate', e.target.value)}
                />
              </div>

              {formData.status === 'INACTIVE' && (
                <div className="modal-form-group">
                  <label className="modal-label">폐점일</label>
                  <input
                    type="date"
                    className="modal-input"
                    value={formData.closedDate}
                    onChange={(e) => handleFormChange('closedDate', e.target.value)}
                  />
                </div>
              )}

              <div className="modal-form-group">
                <label className="modal-label">상태 *</label>
                <div className="modal-select-container">
                  <select
                    className="modal-select"
                    value={formData.status}
                    onChange={(e) => handleFormChange('status', e.target.value)}
                  >
                    <option value="ACTIVE">운영중</option>
                    <option value="INACTIVE">운영중단</option>
                  </select>
                  <span className="ri-arrow-down-s-line modal-select-arrow"></span>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="modal-btn modal-btn-cancel" onClick={handleCloseAllModals}>
                취소
              </button>
              <button className="modal-btn modal-btn-primary" onClick={handleSaveConfirm}>
                확인
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 수정 모달 */}
      {showEditModal && (
        <div className="modal-overlay active" id="edit-branch-modal">
          <div className="modal-container">
            <div className="modal-header">
              <h3 className="modal-title">지점 수정</h3>
              <button className="modal-close" onClick={handleCloseAllModals}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="modal-form-group">
                <label className="modal-label">지점명 *</label>
                <input
                  type="text"
                  className="modal-input"
                  value={formData.name}
                  onChange={(e) => handleFormChange('name', e.target.value)}
                  placeholder="지점명을 입력하세요"
                />
              </div>

              <div className="modal-form-group">
                <label className="modal-label">국가코드 *</label>
                <input
                  type="text"
                  className="modal-input"
                  value={formData.country}
                  onChange={(e) => handleFormChange('country', e.target.value.toUpperCase())}
                  placeholder="예: KR, US, JP"
                  maxLength="2"
                />
              </div>

              <div className="modal-form-group">
                <label className="modal-label">도시</label>
                <input
                  type="text"
                  className="modal-input"
                  value={formData.city}
                  onChange={(e) => handleFormChange('city', e.target.value)}
                  placeholder="예: 서울"
                />
              </div>

              <div className="modal-form-group">
                <label className="modal-label">구/군</label>
                <input
                  type="text"
                  className="modal-input"
                  value={formData.district}
                  onChange={(e) => handleFormChange('district', e.target.value)}
                  placeholder="예: 강남구"
                />
              </div>

              <div className="modal-form-group">
                <label className="modal-label">주소</label>
                <input
                  type="text"
                  className="modal-input"
                  value={formData.address}
                  onChange={(e) => handleFormChange('address', e.target.value)}
                  placeholder="상세 주소를 입력하세요"
                />
              </div>

              <div className="modal-form-group">
                <label className="modal-label">관리자명</label>
                <input
                  type="text"
                  className="modal-input"
                  value={formData.managerName}
                  onChange={(e) => handleFormChange('managerName', e.target.value)}
                  placeholder="관리자 이름"
                />
              </div>

              <div className="modal-form-group">
                <label className="modal-label">연락처</label>
                <input
                  type="text"
                  className="modal-input"
                  value={formData.managerPhone}
                  onChange={(e) => handleFormChange('managerPhone', e.target.value)}
                  placeholder="010-0000-0000"
                />
              </div>

              <div className="modal-form-group">
                <label className="modal-label">이메일</label>
                <input
                  type="email"
                  className="modal-input"
                  value={formData.managerEmail}
                  onChange={(e) => handleFormChange('managerEmail', e.target.value)}
                  placeholder="manager@example.com"
                />
              </div>

              <div className="modal-form-group">
                <label className="modal-label">영업시간</label>
                <input
                  type="text"
                  className="modal-input"
                  value={formData.businessHours}
                  onChange={(e) => handleFormChange('businessHours', e.target.value)}
                  placeholder="예: 월-금 09:00~18:00"
                />
              </div>

              <div className="modal-form-group">
                <label className="modal-label">오픈일</label>
                <input
                  type="date"
                  className="modal-input"
                  value={formData.openDate}
                  onChange={(e) => handleFormChange('openDate', e.target.value)}
                />
              </div>

              {formData.status === 'INACTIVE' && (
                <div className="modal-form-group">
                  <label className="modal-label">폐점일</label>
                  <input
                    type="date"
                    className="modal-input"
                    value={formData.closedDate}
                    onChange={(e) => handleFormChange('closedDate', e.target.value)}
                  />
                </div>
              )}

              <div className="modal-form-group">
                <label className="modal-label">상태 *</label>
                <div className="modal-select-container">
                  <select
                    className="modal-select"
                    value={formData.status}
                    onChange={(e) => handleFormChange('status', e.target.value)}
                  >
                    <option value="ACTIVE">운영중</option>
                    <option value="INACTIVE">운영중단</option>
                  </select>
                  <span className="ri-arrow-down-s-line modal-select-arrow"></span>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="modal-btn modal-btn-cancel" onClick={handleCloseAllModals}>
                취소
              </button>
              <button className="modal-btn modal-btn-primary" onClick={handleSaveConfirm}>
                확인
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 상세정보 모달 */}
      {showInfoModal && selectedBranch && (
        <div className="modal-overlay active" id="branch-detail-modal">
          <div className="modal-container">
            <div className="modal-header">
              <h3 className="modal-title">지점 상세정보</h3>
              <button className="modal-close" onClick={handleCloseAllModals}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="detail-item-container">
                <span className="detail-label">지점 코드</span>
                <span className="detail-content">{selectedBranch.branchCode}</span>
              </div>
              <div className="detail-item-container">
                <span className="detail-label">지점명</span>
                <span className="detail-content">{selectedBranch.name}</span>
              </div>
              <div className="detail-item-container">
                <span className="detail-label">상태</span>
                <span className="detail-content">
                  {selectedBranch.status}
                </span>
              </div>
              <div className="detail-item-container">
                <span className="detail-label">국가</span>
                <span className="detail-content">{selectedBranch.country || '-'}</span>
              </div>
              <div className="detail-item-container">
                <span className="detail-label">도시</span>
                <span className="detail-content">{selectedBranch.city || '-'}</span>
              </div>
              <div className="detail-item-container">
                <span className="detail-label">구/군</span>
                <span className="detail-content">{selectedBranch.district || '-'}</span>
              </div>
              <div className="detail-item-container">
                <span className="detail-label">주소</span>
                <span className="detail-content">{selectedBranch.address || '-'}</span>
              </div>
              <div className="detail-item-container">
                <span className="detail-label">우편번호</span>
                <span className="detail-content">{selectedBranch.postalCode || '-'}</span>
              </div>
              <div className="detail-item-container">
                <span className="detail-label">관리자명</span>
                <span className="detail-content">{selectedBranch.managerName || '-'}</span>
              </div>
              <div className="detail-item-container">
                <span className="detail-label">연락처</span>
                <span className="detail-content">{selectedBranch.managerPhone || '-'}</span>
              </div>
              <div className="detail-item-container">
                <span className="detail-label">이메일</span>
                <span className="detail-content">{selectedBranch.managerEmail || '-'}</span>
              </div>
              <div className="detail-item-container">
                <span className="detail-label">영업시간</span>
                <span className="detail-content">{selectedBranch.businessHours || '-'}</span>
              </div>
              <div className="detail-item-container">
                <span className="detail-label">시간대</span>
                <span className="detail-content">{selectedBranch.timeZone || '-'}</span>
              </div>
              <div className="detail-item-container">
                <span className="detail-label">오픈일</span>
                <span className="detail-content">{formatDate(selectedBranch.openDate) || '-'}</span>
              </div>
              <div className="detail-item-container">
                <span className="detail-label">등록일</span>
                <span className="detail-content">{selectedBranch.createdAt}</span>
              </div>
              <div className="detail-item-container">
                <span className="detail-label">수정일</span>
                <span className="detail-content">{selectedBranch.updatedAt}</span>
              </div>
            </div>
            <div className="modal-footer">
              <button className="modal-btn modal-btn-primary" onClick={handleCloseAllModals}>
                확인
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 삭제 확인 모달 */}
      {showDeleteModal && selectedBranch && (
        <div className="modal-overlay active" id="delete-branch-modal">
          <div className="modal-container">
            <div className="modal-header">
              <h3 className="modal-title">지점 삭제</h3>
              <button className="modal-close" onClick={handleCloseAllModals}>&times;</button>
            </div>
            <div className="modal-body">
              <p className="modal-message">
                <strong>{selectedBranch.name}</strong> 지점을 삭제하시겠습니까?
              </p>
              <p className="modal-sub-message">
                삭제된 지점은 복구할 수 없습니다.
              </p>
            </div>
            <div className="modal-footer">
              <button className="modal-btn modal-btn-cancel" onClick={handleCloseAllModals}>
                취소
              </button>
              <button className="modal-btn modal-btn-primary" onClick={handleDeleteConfirm}>
                확인
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 알림 모달 */}
      {showAlertModal && (
        <div className="modal-overlay active" id="alert-modal">
          <div className="modal-container">
            <div className="modal-header">
              <h3 className="modal-title">알림</h3>
              <button className="modal-close" onClick={handleCloseAllModals}>&times;</button>
            </div>
            <div className="modal-body">
              <p className="modal-message">{alertMessage}</p>
            </div>
            <div className="modal-footer">
              <button className="modal-btn modal-btn-primary" onClick={handleCloseAllModals}>
                확인
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BranchManagement;